import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronUp, Loader2, Plus, Search, Tag, X } from 'lucide-react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { addKnowledgeBadgesWithRetry, classifyTaxonomyFailure } from '../lib/uploadTaxonomy';

const OBSOLETE_BADGE_FIXTURE_NAMES = new Set(['as', 'puy', 'as n puy', 'test packs']);
const SUGGESTION_LIMIT = 6;
const SORTS = { used: 'Most used', recent: 'Recently used', name: 'A–Z' };
const usageLabel = (count) => (count ? `used in ${count}` : 'not used yet');

const BadgeDot = ({ color }) => <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />;

const KnowledgeBadgePicker = ({ value = [], onChange, user, compact = false, inlineCreate = false }) => {
  const [badges, setBadges] = useState([]);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#2563eb');
  const [canManage, setCanManage] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkNames, setBulkNames] = useState('');
  const nameInputRef = useRef(null);
  // Only chosen badges show; everything else is found by search, recent use or Browse all.
  const [usage, setUsage] = useState({ counts: {}, last_used: {}, recent: [] });
  const [usageAvailable, setUsageAvailable] = useState(false);
  const [search, setSearch] = useState('');
  const [browsing, setBrowsing] = useState(false);
  const [browseQuery, setBrowseQuery] = useState('');
  const [sortBy, setSortBy] = useState('used');

  const loadBadges = async () => {
    const [badgesResult, capabilityResult] = await Promise.allSettled([
      axiosInstance.get('/knowledge-badges'),
      axiosInstance.get('/upload-taxonomy/capabilities'),
    ]);
    axiosInstance.get('/knowledge-badges/usage').then(({ data }) => { setUsage({ counts: {}, last_used: {}, recent: [], ...data }); setUsageAvailable(true); }).catch(() => setUsageAvailable(false));
    if (badgesResult.status === 'fulfilled') {
      const badgesResponse = badgesResult.value;
      setBadges((badgesResponse.data || []).filter(
        badge => !OBSOLETE_BADGE_FIXTURE_NAMES.has(String(badge?.name || '').trim().toLowerCase())
      ));
    }
    if (capabilityResult.status === 'fulfilled') {
      const capabilityResponse = capabilityResult.value;
      setCanManage(Boolean(capabilityResponse.data?.can_manage));
    }
  };

  useEffect(() => {
    loadBadges();
  }, []);

  const badgeIds = useMemo(() => Array.isArray(value) ? value : [], [value]);
  const selected = useMemo(() => new Set(badgeIds), [badgeIds]);
  const toggle = (badgeId) => {
    onChange?.(
      selected.has(badgeId)
        ? badgeIds.filter(id => id !== badgeId)
        : [...badgeIds, badgeId]
    );
  };

  const byId = useMemo(() => new Map(badges.map(badge => [badge.id, badge])), [badges]);
  const countOf = (badge) => usage.counts?.[badge.id] || 0;
  const showUsage = (badge) => usageAvailable ? usageLabel(countOf(badge)) : 'Usage unavailable';
  const sortBadges = (list, mode) => [...list].sort((a, b) => {
    if (mode === 'recent') return String(usage.last_used?.[b.id] || '').localeCompare(String(usage.last_used?.[a.id] || '')) || a.name.localeCompare(b.name);
    if (mode === 'name') return a.name.localeCompare(b.name);
    return countOf(b) - countOf(a) || a.name.localeCompare(b.name);
  });
  const matching = (text) => {
    const needle = text.trim().toLowerCase();
    return needle ? badges.filter(badge => badge.name.toLowerCase().includes(needle)) : badges;
  };
  // In the inline form the Badge box both names a new badge and searches existing ones.
  const query = inlineCreate ? name : search;
  const suggestions = query.trim() ? sortBadges(matching(query), 'used').slice(0, SUGGESTION_LIMIT) : [];
  const exactMatch = query.trim() && badges.some(badge => badge.name.trim().toLowerCase() === query.trim().toLowerCase());
  const chosen = badgeIds.map(id => byId.get(id)).filter(Boolean);
  const quickPicks = (usage.recent?.length ? usage.recent.map(id => byId.get(id)) : sortBadges(badges, 'used'))
    .filter(badge => badge && !selected.has(badge.id)).slice(0, SUGGESTION_LIMIT);
  const browseList = sortBadges(matching(browseQuery), sortBy);
  const pick = (badge) => {
    if (!selected.has(badge.id)) onChange?.([...badgeIds, badge.id]);
    if (inlineCreate) setName(''); else setSearch('');
  };

  const createBadge = async () => {
    if (!canManage || saving) return;
    if (!name.trim()) {
      nameInputRef.current?.focus();
      toast.info('Enter a badge name first');
      return;
    }
    const existingBadge = badges.find(badge => badge.name.trim().toLowerCase() === name.trim().toLowerCase());
    if (existingBadge) {
      if (!selected.has(existingBadge.id)) onChange?.([...badgeIds, existingBadge.id]);
      setName('');
      setCreating(false);
      toast.success(`Badge “${existingBadge.name}” selected`);
      return;
    }
    setSaving(true);
    try {
      const response = await axiosInstance.post('/knowledge-badges', {
        name: name.trim(),
        color,
      });
      const badge = response.data;
      setBadges(current => [...current, badge].sort((a, b) => a.name.localeCompare(b.name)));
      onChange?.([...badgeIds, badge.id]);
      setName('');
      setCreating(false);
      toast.success(`Badge “${badge.name}” created`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create badge');
    } finally {
      setSaving(false);
    }
  };

  const createBadgesBulk = async () => {
    if (!canManage || !bulkNames.trim() || saving) return;
    setSaving(true);
    try {
      const response = await addKnowledgeBadgesWithRetry(axiosInstance, bulkNames, color);
      const ready = (response.data?.badges || []).filter(
        badge => !OBSOLETE_BADGE_FIXTURE_NAMES.has(String(badge?.name || '').trim().toLowerCase())
      );
      setBadges(current => {
        const byId = new Map(current.map(badge => [badge.id, badge]));
        ready.forEach(badge => byId.set(badge.id, badge));
        return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
      });
      onChange?.([...new Set([...badgeIds, ...ready.map(badge => badge.id)])]);
      setBulkNames('');
      setBulkMode(false);
      toast.success(`${ready.length} topic${ready.length === 1 ? '' : 's'} ready`);
    } catch (error) {
      const failure = classifyTaxonomyFailure(error, 'Could not add badges/topics');
      toast.error(failure.message);
      if (failure.sessionRevoked) {
        localStorage.removeItem('token');
        window.setTimeout(() => window.location.assign('/auth'), 900);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    createBadge();
  };

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      {inlineCreate && (
        <div>
          <Label className="text-xs">Badge <span className="font-normal text-slate-400">Optional</span></Label>
          {canManage ? <form className="mt-1 flex gap-2" onSubmit={handleCreateSubmit}>
            <Input
              ref={nameInputRef}
              value={name}
              onChange={event => setName(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  createBadge();
                }
              }}
              placeholder="e.g. Consumer Duty"
              maxLength={160}
              className="bg-white"
              disabled={saving}
              data-testid="new-knowledge-badge-name"
            />
            <label
              className="relative flex h-9 w-10 shrink-0 cursor-pointer self-center items-center justify-center rounded-md border border-slate-300 bg-white p-1 shadow-sm hover:border-blue-400"
              title="Choose badge colour"
            >
              <span className="sr-only">Badge colour</span>
              <input
                type="color"
                value={color}
                onChange={event => setColor(event.target.value)}
                disabled={saving}
                className="h-full w-full cursor-pointer border-0 bg-transparent p-0 disabled:cursor-not-allowed"
                aria-label="Badge colour"
                data-testid="inline-badge-colour"
              />
            </label>
            <Button type="submit" size="sm" disabled={saving} className="shrink-0 self-center bg-blue-600 text-white hover:bg-blue-700">
              {saving ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Plus size={13} className="mr-1" />} Add
            </Button>
          </form> : <p className="mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">Choose from the approved badges below.</p>}
        </div>
      )}

      {!inlineCreate && (
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
            <Tag size={13} /> Custom badges
          </Label>
          {!compact && (
            <p className="mt-0.5 text-xs text-gray-400">
              Add reusable labels such as Theme, Legislation, Complaints or any name your organisation chooses.
            </p>
          )}
        </div>
        {canManage && (
          <Button type="button" size="sm" variant="outline" onClick={() => setCreating(current => !current)}>
            {creating ? <X size={13} className="mr-1" /> : <Plus size={13} className="mr-1" />}
            {creating ? 'Cancel' : 'New badge'}
          </Button>
        )}
      </div>
      )}

      {!inlineCreate && creating && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-blue-100 bg-blue-50 p-3">
          <div className="min-w-[180px] flex-1">
            <Label className="text-xs">Badge name</Label>
            <Input
              value={name}
              onChange={event => setName(event.target.value)}
              placeholder="e.g. Consumer Duty"
              maxLength={160}
              className="mt-1 bg-white"
              data-testid="new-knowledge-badge-name"
            />
          </div>
          <div>
            <Label className="text-xs">Colour</Label>
            <input
              type="color"
              value={color}
              onChange={event => setColor(event.target.value)}
              className="mt-1 block h-9 w-12 cursor-pointer rounded border bg-white p-1"
              aria-label="Badge colour"
            />
          </div>
          <Button type="button" size="sm" onClick={createBadge} disabled={!name.trim()}>
            Create and select
          </Button>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="knowledge-badge-picker">
      {canManage && (
        <div className="w-full space-y-2">
          <button type="button" className="text-xs font-medium text-blue-700 hover:underline" onClick={() => setBulkMode(current => !current)}>
            {bulkMode ? 'Close paste list' : 'Paste a list from a spreadsheet'}
          </button>
          {bulkMode && (
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
              <Textarea value={bulkNames} onChange={event => setBulkNames(event.target.value)} placeholder={'Disability Discrimination\nEquality Act 2010\nReasonable Adjustments'} className="min-h-24 bg-white" />
              <p className="mt-1 text-[11px] text-slate-500">Each row, comma, semicolon or spreadsheet cell becomes its own blue topic.</p>
              <Button type="button" size="sm" className="mt-2" onClick={createBadgesBulk} disabled={!bulkNames.trim() || saving}>
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Create and select all
              </Button>
            </div>
          )}
        </div>
      )}

        {badges.length === 0 && (
          <span className="text-xs text-gray-400">
            {canManage ? 'No custom badges yet. Create the first one.' : 'No custom badges are available.'}
          </span>
        )}

        {chosen.length > 0 && <div className="flex w-full flex-wrap gap-1.5" data-testid="chosen-badges" aria-label="Chosen badges">
          {chosen.map(badge => (
            <span key={badge.id} className="inline-flex max-w-full items-center gap-1 rounded-full border py-0.5 pl-2.5 pr-1 text-xs font-medium text-white" style={{ backgroundColor: badge.color, borderColor: badge.color }} title={badge.description || badge.name}>
              <span className="truncate">{badge.name}</span>
              <button type="button" onClick={() => toggle(badge.id)} className="grid h-4 w-4 place-items-center rounded-full hover:bg-black/15" aria-label={`Remove ${badge.name}`}><X size={11} /></button>
            </span>
          ))}
        </div>}

        {badges.length > 0 && !inlineCreate && (
          <div className="relative w-full">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
            <Input value={search} onChange={event => setSearch(event.target.value)} placeholder={`Find a badge (${badges.length})`} className="h-9 bg-white pl-8 text-sm" aria-label="Find a badge" data-testid="badge-search" />
          </div>
        )}

        {query.trim() && badges.length > 0 && (
          <ul className="w-full overflow-hidden rounded-lg border border-slate-200 bg-white" role="listbox" aria-label="Matching badges" data-testid="badge-suggestions">
            {suggestions.map(badge => (
              <li key={badge.id}>
                <button type="button" role="option" aria-selected={selected.has(badge.id)} onClick={() => pick(badge)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-slate-50">
                  <BadgeDot color={badge.color} />
                  <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{badge.name}</span>
                  {selected.has(badge.id) ? <Check size={13} className="text-emerald-600" aria-label="Chosen" /> : <span className="shrink-0 text-slate-400">{showUsage(badge)}</span>}
                </button>
              </li>
            ))}
            {!suggestions.length && <li className="px-3 py-2 text-xs text-slate-500">No badge matches “{query.trim()}”.</li>}
            {canManage && !exactMatch && <li className="border-t border-slate-100 px-3 py-2 text-xs text-blue-700">
              {inlineCreate ? <>Press <strong>Add</strong> to create “{query.trim()}”.</> : <button type="button" className="font-medium hover:underline" onClick={() => { setName(query.trim()); setCreating(true); setSearch(''); }}><Plus size={12} className="mr-1 inline" />Create “{query.trim()}”</button>}
            </li>}
          </ul>
        )}

        {!query.trim() && quickPicks.length > 0 && (
          <div className="flex w-full flex-wrap items-center gap-1.5" data-testid="badge-quick-picks">
            <span className="mr-1 text-[11px] text-slate-400">{usage.recent?.length ? 'Recently used by you' : usageAvailable ? 'Most used' : 'Badges'}</span>
            {quickPicks.map(badge => (
              <button key={badge.id} type="button" onClick={() => toggle(badge.id)} className="max-w-[14rem] truncate rounded-full border bg-white px-2.5 py-0.5 text-xs font-medium text-gray-600 hover:opacity-80" style={{ borderColor: badge.color }} title={`${badge.name} · ${showUsage(badge)}`}>
                {badge.name}
              </button>
            ))}
          </div>
        )}

        {badges.length > 0 && (
          <button type="button" onClick={() => setBrowsing(current => !current)} className="flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline" aria-expanded={browsing} data-testid="badge-browse-toggle">
            {browsing ? <ChevronUp size={13} /> : <ChevronDown size={13} />}{browsing ? 'Close all badges' : `Browse all ${badges.length} badges`}
          </button>
        )}

        {browsing && (
          <div className="w-full rounded-lg border border-slate-200 bg-white" data-testid="badge-browser">
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-2">
              <div className="relative min-w-[10rem] flex-1">
                <Search size={13} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                <Input value={browseQuery} onChange={event => setBrowseQuery(event.target.value)} placeholder="Search all badges" className="h-8 bg-white pl-7 text-xs" aria-label="Search all badges" />
              </div>
              <select value={sortBy} onChange={event => setSortBy(event.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" aria-label="Sort badges">
                {Object.entries(SORTS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
            </div>
            <ul className="max-h-64 overflow-y-auto" aria-label="All badges">
              {browseList.map(badge => {
                const active = selected.has(badge.id);
                return (
                  <li key={badge.id}>
                    <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-50">
                      <input type="checkbox" checked={active} onChange={() => toggle(badge.id)} className="h-3.5 w-3.5 accent-blue-600" />
                      <BadgeDot color={badge.color} />
                      <span className="min-w-0 flex-1 truncate text-slate-800" title={badge.description || badge.name}>{badge.name}</span>
                      <span className="shrink-0 text-slate-400">{usageAvailable ? countOf(badge) || '–' : '–'}</span>
                    </label>
                  </li>
                );
              })}
              {!browseList.length && <li className="px-3 py-3 text-xs text-slate-500">No badge matches “{browseQuery.trim()}”.</li>}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBadgePicker;
